'use client';

/**
 * LaunchDarkly **initialization readiness** for the React tree.
 *
 * - **`useFlags`** comes from **`launchdarkly-react-client-sdk`** (not defined in this repo). Use it anywhere
 *   under `LDProvider` / `LaunchDarklyFlagsWithStatusProvider` for the current flag snapshot.
 * - **`useLaunchDarklyFlagsReady`** reads boolean readiness from context supplied by
 *   `LaunchDarklyFlagsReadyProvider` (typically via `LaunchDarklyFlagsWithStatusProvider`).
 * - **`useFlagsAndLoadingState`** composes **`useLaunchDarklyFlagsReady`** + SDK **`useFlags`** when you need
 *   both “ready” and flags together.
 */
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { isNil } from 'es-toolkit';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';

// Arbitrary time in seconds to wait for LaunchDarkly to initialize before treating flags as ready.
const ldTimeoutSeconds = 3;

// Exported so non-runtime trees (Storybook stories, tests) can wrap with
// a fixed-value Provider without dragging in the LDProvider + readiness
// machinery.
export const LaunchDarklyFlagsReadyContext = createContext<boolean | null>(null);

/**
 * Subscribes to LaunchDarkly client readiness via the SDK (`useLDClient`, `waitForInitialization`).
 * Feeds `LaunchDarklyFlagsReadyProvider`; not meant for direct use in feature code.
 */
function useLaunchDarklyInitializationReady() {
  const ldClient = useLDClient();
  const [flagsReady, setFlagsReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    if (isNil(ldClient)) {
      const fallbackTimer = setTimeout(() => mounted && setFlagsReady(true), ldTimeoutSeconds * 1000);
      return () => {
        clearTimeout(fallbackTimer);
        mounted = false;
      };
    }

    void ldClient
      .waitForInitialization(ldTimeoutSeconds)
      .then(() => mounted && setFlagsReady(true))
      .catch(() => mounted && setFlagsReady(true));

    return () => {
      mounted = false;
    };
  }, [ldClient]);

  return flagsReady;
}

type LaunchDarklyFlagsReadyProviderProps = {
  readonly children: ReactNode;
};

export function LaunchDarklyFlagsReadyProvider({ children }: LaunchDarklyFlagsReadyProviderProps) {
  const flagsReady = useLaunchDarklyInitializationReady();

  return <LaunchDarklyFlagsReadyContext.Provider value={flagsReady}>{children}</LaunchDarklyFlagsReadyContext.Provider>;
}

/**
 * Returns whether LaunchDarkly has finished initializing (same semantics as
 * `useLaunchDarklyInitializationReady`), for components under `LaunchDarklyFlagsReadyProvider`.
 * If you also need flag values, prefer `useFlagsAndLoadingState`.
 */
export function useLaunchDarklyFlagsReady() {
  const value = useContext(LaunchDarklyFlagsReadyContext);
  if (isNil(value)) {
    throw new Error('useLaunchDarklyFlagsReady must be used within LaunchDarklyFlagsReadyProvider');
  }
  return value;
}

/**
 * Returns `{ flagsReady, flags }`—same `flagsReady` as `LaunchDarklyFlagsReadyProvider` /
 * `waitForInitialization`, plus SDK `useFlags()`.
 *
 * Use this when you need an **intermediate UI** (loader, placeholder) **before** branching on flags, or
 * when pre-init flag defaults would cause bad side effects (redirects, access checks). If a **small
 * first-load flash** or running logic as soon as `useFlags()` updates is acceptable, **`useFlags()`**
 * alone is enough.
 *
 * Must run under `LaunchDarklyFlagsWithStatusProvider` (or `LDProvider` + `LaunchDarklyFlagsReadyProvider`).
 */
export function useFlagsAndLoadingState() {
  const flagsReady = useLaunchDarklyFlagsReady();
  const flags = useFlags();
  return { flagsReady, flags };
}
